import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import Hls from "hls.js";
import { apiFetch } from "@/lib/api-client";
import { Skeleton } from "@/components/skeleton";
import { useWebinarRealtime } from "@/hooks/use-webinar-realtime";
import type { WebinarChatMessage } from "@/hooks/use-webinar-realtime";
import type { WebinarLayoutPreset } from "@ease/shared";
import { formatCurrency } from "@/lib/i18n";
import { OfferOverlay } from "@/components/webinar/offer-overlay";
import { CanvasRenderer } from "@/components/webinar/canvas-renderer";
import { CaptionAttendeeOverlay } from "./captions";

type ChatComposerStatus = "pending" | "sent" | "failed";

interface LocalChatMessage {
  localId: string;
  serverMessageId?: string;
  senderName: string;
  senderRole: "attendee";
  content: string;
  createdAt: string;
  shadowFiltered?: boolean;
  status: ChatComposerStatus;
}

type DisplayChatMessage = (WebinarChatMessage & { id: string }) | (LocalChatMessage & { id: string });

/**
 * G.5: Live Broadcast Room — Attendee View
 * Per WEBINAR-ARCHITECTURE.md: Viewers NEVER connect to LiveKit.
 * They watch HLS from Cloudflare CDN with ~15-30s latency.
 * Sidebar tabs (Chat/Q&A/Polls/CTA) run on the canonical webinar realtime seam.
 */
export default function LiveAttendeePage() {
  const { webinarId } = useParams();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hlsUrl, setHlsUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"chat" | "qa" | "polls" | "cta" | "leaderboard">("chat");
  const [chatDraft, setChatDraft] = useState("");
  const [questionDraft, setQuestionDraft] = useState("");
  const [selectedPollOption, setSelectedPollOption] = useState<string | null>(null);
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [ctaNow, setCtaNow] = useState(() => Date.now());
  const [handRaiseError, setHandRaiseError] = useState<string | null>(null);
  const [isHandRaisePending, setIsHandRaisePending] = useState(false);
  const [optimisticMessages, setOptimisticMessages] = useState<LocalChatMessage[]>([]);
  const [isChatScrollLocked, setIsChatScrollLocked] = useState(false);
  const [reportedMessageIds, setReportedMessageIds] = useState<Set<string>>(() => new Set());
  const [reportPendingMessageId, setReportPendingMessageId] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const chatListRef = useRef<HTMLDivElement>(null);
  const optimisticCleanupTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const isMountedRef = useRef(true);
  const {
    currentParticipantId,
    messages,
    activePoll,
    pollResultsById,
    questions,
    activeCta,
    recentPurchases,
    leaderboard,
    achievementToasts,
    activeSlideState,
    attendeeCount,
    raisedHands,
    spotlight,
    recentReactions,
    layout,
    isConnected,
    connectError,
    chatError,
    questionError,
    sendChatMessage,
    sendReaction,
    submitPollResponse,
    submitQuestion,
    upvoteQuestion,
    trackCtaClick,
    raiseHand,
    lowerHand,
    dismissCta,
  } = useWebinarRealtime(webinarId);

  const isCurrentUserRaised = currentParticipantId
    ? raisedHands.some((entry) => entry.participantId === currentParticipantId)
    : false;
  const isCurrentUserSpotlighted = currentParticipantId != null && spotlight.participantId === currentParticipantId;

  const chatMessages = useMemo(
    () => mergeRealtimeAndOptimisticMessages(messages, optimisticMessages),
    [messages, optimisticMessages],
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      for (const timer of optimisticCleanupTimersRef.current) {
        clearTimeout(timer);
      }
      optimisticCleanupTimersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!activeCta?.countdownEndsAt) {
      return;
    }

    setCtaNow(Date.now());
    const timer = setInterval(() => {
      setCtaNow(Date.now());
    }, 1_000);

    return () => clearInterval(timer);
  }, [activeCta?.countdownEndsAt]);

  // Fetch HLS URL
  useEffect(() => {
    if (!webinarId) return;

    async function fetchHls() {
      try {
        const data = await apiFetch<{ hlsUrl: string }>(`/v1/webinars/${webinarId}/hls-url`);
        setHlsUrl(data.hlsUrl);
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Webinar not available");
        setIsLoading(false);
      }
    }

    fetchHls();
  }, [webinarId]);

  // Initialize HLS.js player
  useEffect(() => {
    if (!hlsUrl || !videoRef.current) return;

    const video = videoRef.current;

    if (Hls.isSupported()) {
      const hls = new Hls({
        lowLatencyMode: true,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 10,
      });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {
          // Autoplay blocked — user needs to click
        });
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          setError("Stream connection lost. Retrying...");
          hls.startLoad();
        }
      });

      return () => hls.destroy();
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS
      video.src = hlsUrl;
      video.addEventListener("loadedmetadata", () => {
        video.play().catch(() => {});
      });
    }
  }, [hlsUrl]);

  useEffect(() => {
    const container = chatListRef.current;
    if (!container || isChatScrollLocked) {
      return;
    }
    container.scrollTop = container.scrollHeight;
  }, [chatMessages, isChatScrollLocked]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="w-full max-w-5xl space-y-4 px-6">
          <Skeleton className="h-[420px] w-full rounded-2xl bg-gray-800" />
          <div className="flex items-center justify-center gap-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-10 w-10 rounded-full bg-gray-800" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="text-center">
          <p className="text-lg text-white">{error}</p>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">The webinar may not have started yet.</p>
        </div>
      </div>
    );
  }

  const layoutShellClass = getLayoutShellClass(layout.preset);
  const sidebarClass = getSidebarClass(layout.preset);
  const stageClass = getStageClass(layout.preset);
  const layoutLabel = layout.preset.replace(/_/g, " ");
  const currentSlide = activeSlideState?.currentSlide ?? null;
  const pointsByParticipant = new Map(leaderboard.map((entry) => [entry.participantId, entry.points]));

  const handleChatScroll = () => {
    const container = chatListRef.current;
    if (!container) {
      return;
    }
    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
    setIsChatScrollLocked(remaining > 32);
  };

  const jumpToLatestChat = () => {
    const container = chatListRef.current;
    if (!container) {
      return;
    }
    container.scrollTop = container.scrollHeight;
    setIsChatScrollLocked(false);
  };

  const scheduleOptimisticMessageRemoval = (localId: string) => {
    const timer = setTimeout(() => {
      if (!isMountedRef.current) {
        return;
      }
      optimisticCleanupTimersRef.current = optimisticCleanupTimersRef.current.filter((current) => current !== timer);
      setOptimisticMessages((current) => current.filter((message) => message.localId !== localId));
    }, 1200);
    optimisticCleanupTimersRef.current.push(timer);
  };

  const handleSendChat = async () => {
    const value = chatDraft.trim();
    if (!value) {
      return;
    }

    const optimisticId = createLocalChatId();
    const optimisticMessage: LocalChatMessage = {
      localId: optimisticId,
      senderName: "You",
      senderRole: "attendee",
      content: value,
      createdAt: new Date().toISOString(),
      status: "pending",
    };

    setOptimisticMessages((current) => [...current, optimisticMessage]);
    setChatDraft("");
    setIsChatScrollLocked(false);

    const sent = await sendChatMessage(value);
    if (!isMountedRef.current) {
      return;
    }

    if (sent) {
      setOptimisticMessages((current) => current.map((message) => (
        message.localId === optimisticId
          ? {
              ...message,
              serverMessageId: sent.id,
              shadowFiltered: sent.shadowFiltered,
              status: "sent",
            }
          : message
      )));

      scheduleOptimisticMessageRemoval(optimisticId);
      return;
    }

    setOptimisticMessages((current) => current.map((message) => (
      message.localId === optimisticId
        ? { ...message, status: "failed" }
        : message
    )));
  };

  const handleRetryChat = async (localId: string) => {
    const pending = optimisticMessages.find((message) => message.localId === localId);
    if (!pending) {
      return;
    }

    setOptimisticMessages((current) => current.map((message) => (
      message.localId === localId
        ? { ...message, status: "pending", createdAt: new Date().toISOString() }
        : message
    )));

    const sent = await sendChatMessage(pending.content);
    if (!isMountedRef.current) {
      return;
    }

    if (sent) {
      setOptimisticMessages((current) => current.map((message) => (
        message.localId === localId
          ? {
              ...message,
              serverMessageId: sent.id,
              shadowFiltered: sent.shadowFiltered,
              status: "sent",
            }
          : message
      )));

      scheduleOptimisticMessageRemoval(localId);
      return;
    }

    setOptimisticMessages((current) => current.map((message) => (
      message.localId === localId
        ? { ...message, status: "failed" }
        : message
    )));
  };

  const handleReportChatMessage = async (message: WebinarChatMessage & { id: string }) => {
    if (!webinarId || !message.senderId || message.senderId === currentParticipantId || reportedMessageIds.has(message.id)) {
      return;
    }

    setReportError(null);
    setReportPendingMessageId(message.id);

    try {
      await apiFetch(`/v1/webinars/${webinarId}/chat/moderation/reports`, {
        method: "POST",
        body: JSON.stringify({
          participantId: message.senderId,
          messageId: message.id,
          reasonCode: "attendee_reported",
        }),
      });
      if (!isMountedRef.current) {
        return;
      }
      setReportedMessageIds((current) => {
        const next = new Set(current);
        next.add(message.id);
        return next;
      });
    } catch (reportFailure) {
      if (!isMountedRef.current) {
        return;
      }
      setReportError(reportFailure instanceof Error ? reportFailure.message : "Could not submit report.");
    } finally {
      if (isMountedRef.current) {
        setReportPendingMessageId(null);
      }
    }
  };

  const handleToggleHandRaise = async () => {
    setIsHandRaisePending(true);
    setHandRaiseError(null);
    try {
      if (isCurrentUserRaised) {
        await lowerHand();
      } else {
        await raiseHand();
      }
    } catch (error) {
      setHandRaiseError(error instanceof Error ? error.message : "Unable to update hand raise state");
    } finally {
      setIsHandRaisePending(false);
    }
  };

  return (
    <div
      data-testid="live-program-output-shell"
      data-layout-preset={layout.preset}
      className={`flex h-screen bg-black transition-all duration-500 ${layoutShellClass}`}
    >
      {/* Video area */}
      <div className={`flex min-w-0 flex-1 flex-col ${stageClass}`}>
        <div className="relative flex-1 bg-black">
          {currentSlide ? (
            <div className={`grid h-full w-full gap-3 p-3 ${getSlideStageGrid(layout.preset)}`}>
              <div className="relative min-h-0 overflow-hidden rounded-2xl border border-white/10 bg-black/40">
                <img
                  src={currentSlide.url}
                  alt={currentSlide.name}
                  className="h-full w-full object-contain"
                />
                <div className="pointer-events-none absolute left-4 top-4 rounded-full bg-black/70 px-3 py-1 text-xs font-medium text-white">
                  Slide {Math.min((activeSlideState?.currentIndex ?? 0) + 1, activeSlideState?.slides.length ?? 1)}
                  {activeSlideState?.slides.length ? ` / ${activeSlideState.slides.length}` : ""}
                </div>
              </div>
              <div className={`relative min-h-0 overflow-hidden rounded-2xl border border-white/10 bg-black/60 ${getVideoSurfaceClass(layout.preset)}`}>
                <video
                  ref={videoRef}
                  className="h-full w-full object-contain"
                  playsInline
                  controls
                />
                {webinarId ? <CanvasRenderer webinarId={webinarId} mode="viewer" variant="overlay" /> : null}
                {webinarId ? <CaptionAttendeeOverlay webinarId={webinarId} /> : null}
              </div>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                className="h-full w-full object-contain"
                playsInline
                controls
              />
              {webinarId ? <CanvasRenderer webinarId={webinarId} mode="viewer" variant="overlay" /> : null}
              {webinarId ? <CaptionAttendeeOverlay webinarId={webinarId} /> : null}
            </>
          )}
          <div className="pointer-events-none absolute left-4 top-4 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-black/70 px-3 py-1 text-xs font-medium text-white">
              {attendeeCount} live
            </span>
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${isConnected ? "bg-emerald-500/20 text-emerald-200" : "bg-amber-500/20 text-amber-200"}`}>
              {isConnected ? "Realtime connected" : "Realtime reconnecting"}
            </span>
            <span
              data-testid="live-program-layout-label"
              className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white capitalize"
            >
              {layoutLabel}
            </span>
            {currentSlide ? (
              <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white">
                {activeSlideState?.title ?? "Live slides"}
              </span>
            ) : null}
            {spotlight.participantId ? (
              <span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-medium text-primary">
                Spotlight: {spotlight.displayName ?? "Audience guest"}
              </span>
            ) : null}
          </div>

          <OfferOverlay
            cta={activeCta}
            now={ctaNow}
            recentPurchases={recentPurchases}
            onDismiss={dismissCta}
            onTrackClick={trackCtaClick}
          />
          <div className="absolute left-4 top-16 z-20 flex max-w-sm flex-col gap-2">
            {isCurrentUserSpotlighted ? (
              <div className="rounded-xl border border-primary/25 bg-black/85 px-4 py-3 text-sm text-white shadow-xl backdrop-blur animate-in slide-in-from-left-3">
                <p className="font-medium">You have been spotlighted by the host.</p>
                <p className="mt-1 text-xs text-gray-300">Your stage invitation is active.</p>
              </div>
            ) : null}
            {achievementToasts.map((achievement) => (
              <div
                key={`${achievement.participantId}-${achievement.timestamp}`}
                className="rounded-xl border border-primary/20 bg-black/85 px-4 py-3 text-sm text-white shadow-xl backdrop-blur animate-in slide-in-from-left-3"
              >
                <p className="font-medium">{achievement.displayName} unlocked {achievement.label}</p>
                <p className="mt-1 text-xs text-gray-300">{achievement.points} points total</p>
              </div>
            ))}
          </div>
        </div>

        {/* Emoji reaction bar */}
        <div className="flex flex-col gap-2 bg-gray-900 py-2">
          <div className="flex min-h-7 items-center justify-center gap-2 px-4 text-lg">
            {recentReactions.slice(-6).map((reaction) => (
              <span key={reaction.timestamp} className="animate-in fade-in zoom-in-95 rounded-full bg-white/10 px-2 py-1 text-sm">
                {reactionToEmoji(reaction.reactionType)}
              </span>
            ))}
          </div>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => void handleToggleHandRaise()}
              disabled={isHandRaisePending}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] transition ${
                isCurrentUserRaised
                  ? "border-primary/40 bg-primary/15 text-primary"
                  : "border-white/15 bg-white/5 text-white hover:bg-white/10"
              } disabled:opacity-50`}
            >
              {isCurrentUserRaised ? "Lower hand" : "Raise hand"}
            </button>
            {([
              { emoji: "🔥", type: "fire" },
              { emoji: "👏", type: "clap" },
              { emoji: "❤️", type: "heart" },
              { emoji: "😂", type: "laugh" },
              { emoji: "🎉", type: "party" },
              { emoji: "👍", type: "thumbs_up" },
            ] as const).map((entry) => (
              <button
                key={entry.type}
                type="button"
                onClick={() => void sendReaction(entry.type)}
                className="stagger-item rounded-full px-3 py-1 text-xl transition-transform hover:scale-125 active:scale-90"
                title={`React with ${entry.emoji}`}
              >
                {entry.emoji}
              </button>
            ))}
          </div>
          {handRaiseError ? (
            <p className="text-center text-xs text-rose-300">{handRaiseError}</p>
          ) : null}
        </div>
      </div>

      {/* Sidebar — mounted on canonical webinar realtime seam */}
      <div className={`flex border-l border-gray-800 bg-gray-900 transition-all duration-500 ${sidebarClass}`}>
        {/* Tabs */}
        <div className="flex border-b border-gray-800">
          {(["chat", "qa", "polls", "cta", "leaderboard"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`stagger-item flex-1 px-2 py-2.5 text-xs font-medium transition-colors ${
                activeTab === tab
                  ? "border-b-2 border-primary text-white"
                  : "text-[var(--color-muted-foreground)] hover:text-gray-300"
              }`}
            >
              {tab === "qa" ? "Q&A" : tab === "cta" ? "CTA" : tab === "leaderboard" ? "Leaders" : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {activeTab === "chat" && (
            <div className="flex h-full flex-col">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                  Live chat
                </p>
                {isChatScrollLocked ? (
                  <button
                    type="button"
                    onClick={jumpToLatestChat}
                    className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary"
                  >
                    Scroll locked • Jump to latest
                  </button>
                ) : null}
              </div>
              <div
                ref={chatListRef}
                onScroll={handleChatScroll}
                className="space-y-3 overflow-y-auto"
              >
                {chatMessages.length === 0 ? (
                  <p className="text-center text-xs text-[var(--color-muted-foreground)]">No chat messages yet.</p>
                ) : chatMessages.map((message) => {
                  const canReport = isReportableRealtimeChatMessage(message, currentParticipantId);
                  const reportSubmitted = reportedMessageIds.has(message.id);
                  const isReportPending = reportPendingMessageId === message.id;

                  return (
                    <div key={message.id} className="rounded-xl border border-gray-800 bg-gray-950/60 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-white">{message.senderName}</p>
                        <div className="flex items-center gap-2">
                          {"senderId" in message && message.senderId && pointsByParticipant.has(message.senderId) ? (
                            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                              {pointsByParticipant.get(message.senderId)} pts
                            </span>
                          ) : null}
                          {message.shadowFiltered ? (
                            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-200">
                              Only visible to you
                            </span>
                          ) : null}
                          {"status" in message ? (
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                              message.status === "failed"
                                ? "bg-rose-500/15 text-rose-200"
                                : message.status === "sent"
                                  ? "bg-emerald-500/15 text-emerald-200"
                                  : "bg-white/10 text-gray-200"
                            }`}>
                              {message.status}
                            </span>
                          ) : null}
                          <span className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
                            {message.senderRole.replace("_", " ")}
                          </span>
                        </div>
                      </div>
                      <p className="mt-2 text-sm text-gray-300">{message.content}</p>
                      {canReport ? (
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <p className="text-[11px] text-[var(--color-muted-foreground)]">
                            Help moderators by reporting unsafe or abusive chat.
                          </p>
                          <button
                            type="button"
                            disabled={reportSubmitted || isReportPending}
                            onClick={() => void handleReportChatMessage(message)}
                            className="rounded-full border border-amber-400/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-100 disabled:cursor-not-allowed disabled:border-emerald-400/30 disabled:text-emerald-200"
                          >
                            {reportSubmitted ? "Reported" : isReportPending ? "Reporting" : "Report"}
                          </button>
                        </div>
                      ) : null}
                      {"status" in message && message.status === "failed" ? (
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <p className="text-xs text-rose-300">Failed to send. Check your connection and retry.</p>
                          <button
                            type="button"
                            onClick={() => void handleRetryChat(message.localId)}
                            className="rounded-full border border-rose-400/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-rose-200"
                          >
                            Retry
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {reportError ? <p className="mt-2 text-xs text-rose-300">{reportError}</p> : null}
              <form
                className="mt-4 flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleSendChat();
                }}
              >
                <textarea
                  value={chatDraft}
                  onChange={(event) => setChatDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      void handleSendChat();
                    }
                  }}
                  rows={2}
                  placeholder="Type a message. Enter sends, Shift+Enter adds a new line."
                  className="min-h-[52px] w-full resize-none rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder:text-[var(--color-muted-foreground)]"
                />
                <button
                  type="submit"
                  className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
                >
                  Send
                </button>
              </form>
              {connectError ? (
                <p className="mt-2 text-xs text-amber-300">{connectError}</p>
              ) : null}
              {chatError ? (
                <p className="mt-2 text-xs text-rose-300">{chatError}</p>
              ) : null}
            </div>
          )}
          {activeTab === "qa" && (
            <div className="flex h-full flex-col">
              <div className="space-y-3 overflow-y-auto">
                {questions.length === 0 ? (
                  <p className="text-center text-xs text-[var(--color-muted-foreground)]">No audience questions yet.</p>
                ) : questions.map((question) => (
                  <div key={question.id} className="rounded-xl border border-gray-800 bg-gray-950/60 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          {question.isPinned ? (
                            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                              Pinned
                            </span>
                          ) : null}
                          {pointsByParticipant.has(question.contactId) ? (
                            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                              {pointsByParticipant.get(question.contactId)} pts
                            </span>
                          ) : null}
                          <span className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
                            Audience question
                          </span>
                        </div>
                        <p className="mt-2 text-sm font-medium text-white">{question.question}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => void upvoteQuestion(question.id)}
                        className="rounded-full border border-gray-700 px-3 py-1 text-xs font-medium text-gray-200 transition hover:border-primary hover:text-white"
                      >
                        ↑ {question.upvotes}
                      </button>
                    </div>
                    {question.answer ? (
                      <div className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3">
                        <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-300">Host reply</p>
                        <p className="mt-2 text-sm text-gray-200">{question.answer}</p>
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">Waiting for a host reply.</p>
                    )}
                  </div>
                ))}
              </div>
              <form
                className="mt-4 flex flex-col gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  const value = questionDraft.trim();
                  if (!value) return;
                  void submitQuestion(value).then((submitted) => {
                    if (submitted) {
                      setQuestionDraft("");
                    }
                  });
                }}
              >
                <textarea
                  value={questionDraft}
                  onChange={(event) => setQuestionDraft(event.target.value)}
                  rows={3}
                  placeholder="Ask the presenter a question..."
                  className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder:text-[var(--color-muted-foreground)]"
                />
                <button
                  type="submit"
                  className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
                >
                  Submit question
                </button>
              </form>
              {questionError ? (
                <p className="mt-2 text-xs text-rose-300">{questionError}</p>
              ) : null}
            </div>
          )}
          {activeTab === "polls" && (
            <div className="space-y-3">
              {!activePoll ? (
                <p className="text-center text-xs text-[var(--color-muted-foreground)]">No active poll right now.</p>
              ) : (
                <div className="rounded-2xl border border-gray-800 bg-gray-950/60 p-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-primary/80">Live poll</p>
                  <h3 className="mt-2 text-base font-semibold text-white">{activePoll.question}</h3>

                  {(activePoll.pollType === "single_choice" || activePoll.pollType === "multiple_choice") && Array.isArray(activePoll.options) ? (
                    <div className="mt-4 space-y-2">
                      {activePoll.options.map((option) => (
                        <label key={option.id} className="flex items-center gap-2 rounded-xl border border-gray-800 px-3 py-2 text-sm text-gray-200">
                          <input
                            type="radio"
                            name="poll-option"
                            checked={selectedPollOption === option.id}
                            onChange={() => setSelectedPollOption(option.id)}
                          />
                          <span>{option.text}</span>
                        </label>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          if (!selectedPollOption) return;
                          void submitPollResponse(activePoll.id, { selectedOptionIds: [selectedPollOption] });
                        }}
                        className="mt-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
                      >
                        Submit response
                      </button>
                    </div>
                  ) : null}

                  {activePoll.pollType === "rating" ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {Array.from({ length: 10 }).map((_, index) => {
                        const value = index + 1;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => {
                              setSelectedRating(value);
                              void submitPollResponse(activePoll.id, { ratingValue: value });
                            }}
                            className={`rounded-full px-3 py-1.5 text-sm ${selectedRating === value ? "bg-primary text-white" : "bg-gray-800 text-gray-200"}`}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}

                  {pollResultsById[activePoll.id] ? (
                    <div className="mt-5 border-t border-gray-800 pt-4">
                      <p className="text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                        Live results • {pollResultsById[activePoll.id]?.totalResponses ?? 0} responses
                      </p>
                      <div className="mt-3 space-y-2">
                        {pollResultsById[activePoll.id]?.counts?.map((count) => (
                          <div key={count.optionId}>
                            <div className="flex items-center justify-between text-xs text-gray-300">
                              <span>{count.label}</span>
                              <span>{count.responses}</span>
                            </div>
                            <div className="mt-1 h-2 rounded-full bg-gray-800">
                              <div
                                className="h-2 rounded-full bg-primary"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (count.responses / Math.max(pollResultsById[activePoll.id]?.totalResponses ?? 1, 1)) * 100,
                                  )}%`,
                                }}
                              />
                            </div>
                          </div>
                        ))}
                        {typeof pollResultsById[activePoll.id]?.average === "number" ? (
                          <p className="text-sm text-gray-200">
                            Average rating: {pollResultsById[activePoll.id]?.average}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
          {activeTab === "cta" && (
            <div className="space-y-3">
              {!activeCta ? (
                <div className="rounded-2xl border border-dashed border-gray-700 bg-gray-950/40 p-6 text-center">
                  <p className="text-sm font-medium text-white">No live offer right now.</p>
                  <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                    The presenter will push the next CTA here when the sequence goes live.
                  </p>
                </div>
              ) : (
                <div className="rounded-2xl border border-gray-800 bg-gray-950/60 p-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-primary/80">Live CTA</p>
                  <h3 className="mt-2 text-base font-semibold text-white">{activeCta.headline ?? activeCta.label}</h3>
                  {activeCta.description ? (
                    <p className="mt-2 text-sm text-gray-300">{activeCta.description}</p>
                  ) : null}
                  {activeCta.productImageUrl ? (
                    <img
                      src={activeCta.productImageUrl}
                      alt={activeCta.label}
                      className="mt-4 h-40 w-full rounded-xl object-cover"
                    />
                  ) : null}
                  {(activeCta.offerPriceCents != null || activeCta.originalPriceCents != null) ? (
                    <div className="mt-4 flex items-end gap-3">
                      {activeCta.offerPriceCents != null ? (
                        <span className="text-2xl font-semibold text-white">
                          {formatCurrency(activeCta.offerPriceCents, activeCta.currency, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                        </span>
                      ) : null}
                      {activeCta.originalPriceCents != null ? (
                        <span className="text-sm text-gray-400 line-through">
                          {formatCurrency(activeCta.originalPriceCents, activeCta.currency, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    {activeCta.countdownEndsAt ? (
                      <div className="rounded-xl border border-gray-800 bg-black/30 p-3">
                        <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Countdown</p>
                        <p className="mt-1 text-lg font-semibold text-white">
                          {formatCountdown(activeCta.countdownEndsAt, ctaNow)}
                        </p>
                      </div>
                    ) : null}
                    {activeCta.scarcityCount != null ? (
                      <div className="rounded-xl border border-gray-800 bg-black/30 p-3">
                        <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Availability</p>
                        <p className="mt-1 text-lg font-semibold text-white">
                          {activeCta.scarcityCount} left
                        </p>
                      </div>
                    ) : null}
                  </div>
                  {activeCta.bonuses?.length ? (
                    <div className="mt-4">
                      <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Bonuses</p>
                      <div className="mt-2 space-y-2">
                        {activeCta.bonuses.map((bonus) => (
                          <div key={bonus.name} className="flex items-center justify-between rounded-xl border border-gray-800 px-3 py-2 text-sm text-gray-200">
                            <span>{bonus.name}</span>
                            <span>{formatCurrency(bonus.valueCents, activeCta.currency, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {activeCta.testimonials?.length ? (
                    <div className="mt-4 space-y-2">
                      {activeCta.testimonials.slice(0, 2).map((testimonial) => (
                        <blockquote key={`${testimonial.name}-${testimonial.text}`} className="rounded-xl border border-gray-800 bg-black/30 p-3">
                          <p className="text-sm text-gray-200">“{testimonial.text}”</p>
                          <footer className="mt-2 text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                            {testimonial.name}
                          </footer>
                        </blockquote>
                      ))}
                    </div>
                  ) : null}
                  {activeCta.url ? (
                    <a
                      href={activeCta.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => void trackCtaClick(activeCta)}
                      className="mt-4 inline-flex rounded-full bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
                    >
                      Open offer
                    </a>
                  ) : null}
                </div>
              )}
            </div>
          )}
          {activeTab === "leaderboard" && (
            <div className="space-y-3">
              {leaderboard.length === 0 ? (
                <p className="text-center text-xs text-[var(--color-muted-foreground)]">Leaderboard is warming up.</p>
              ) : (
                leaderboard.map((entry, index) => (
                  <div key={entry.participantId} className="rounded-xl border border-gray-800 bg-gray-950/60 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-white">#{index + 1} {entry.displayName}</p>
                        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                          {entry.achievements.length > 0 ? entry.achievements.join(" • ") : "No achievements yet"}
                        </p>
                      </div>
                      <span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                        {entry.points} pts
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function createLocalChatId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function mergeRealtimeAndOptimisticMessages(
  realtimeMessages: WebinarChatMessage[],
  optimisticMessages: LocalChatMessage[],
): DisplayChatMessage[] {
  const optimisticServerIds = new Set(
    optimisticMessages
      .map((message) => message.serverMessageId)
      .filter((value): value is string => typeof value === "string" && value.length > 0),
  );

  const rows = [
    ...realtimeMessages
      .filter((message) => !optimisticServerIds.has(message.id))
      .map((message) => ({
        ...message,
        id: message.id,
      })),
    ...optimisticMessages.map((message) => ({
      ...message,
      id: message.localId,
    })),
  ];

  return rows.sort((left, right) => (
    new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  ));
}

function isReportableRealtimeChatMessage(
  message: DisplayChatMessage,
  currentParticipantId: string | null | undefined,
): message is WebinarChatMessage & { id: string } {
  return "senderId" in message
    && typeof message.senderId === "string"
    && message.senderId.length > 0
    && message.senderId !== currentParticipantId
    && !("status" in message);
}

function getLayoutShellClass(preset: WebinarLayoutPreset): string {
  switch (preset) {
    case "gallery":
    case "panel_discussion":
      return "flex-col";
    default:
      return "flex-row";
  }
}

function getStageClass(preset: WebinarLayoutPreset): string {
  switch (preset) {
    case "slides_only":
    case "presentation_focus":
    case "cinema":
      return "bg-black";
    case "picture_in_picture":
      return "bg-black px-3 py-3";
    default:
      return "bg-black";
  }
}

function getSidebarClass(preset: WebinarLayoutPreset): string {
  switch (preset) {
    case "speaker_only":
      return "w-72";
    case "speaker_slides":
    case "picture_in_picture":
    case "interview":
      return "w-80";
    case "slides_only":
    case "presentation_focus":
    case "cinema":
      return "w-96";
    case "gallery":
    case "panel_discussion":
      return "w-full h-[22rem] border-l-0 border-t";
    case "side_by_side":
      return "w-[28rem]";
    default:
      return "w-80";
  }
}

function getSlideStageGrid(preset: WebinarLayoutPreset): string {
  switch (preset) {
    case "side_by_side":
      return "grid-cols-2";
    case "slides_only":
    case "presentation_focus":
    case "speaker_slides":
      return "grid-cols-1";
    default:
      return "grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.6fr)]";
  }
}

function getVideoSurfaceClass(preset: WebinarLayoutPreset): string {
  switch (preset) {
    case "slides_only":
      return "h-40 w-64 justify-self-end self-end shadow-2xl";
    case "presentation_focus":
    case "speaker_slides":
      return "h-48 w-72 justify-self-end self-end shadow-2xl";
    default:
      return "";
  }
}

function formatCountdown(countdownEndsAt: string, now: number) {
  const remainingMs = Math.max(0, new Date(countdownEndsAt).getTime() - now);
  const totalSeconds = Math.ceil(remainingMs / 1_000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function reactionToEmoji(reactionType: "fire" | "clap" | "heart" | "laugh" | "party" | "thumbs_up") {
  switch (reactionType) {
    case "fire":
      return "🔥";
    case "clap":
      return "👏";
    case "heart":
      return "❤️";
    case "laugh":
      return "😂";
    case "party":
      return "🎉";
    case "thumbs_up":
      return "👍";
    default:
      return "✨";
  }
}
